import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { isActiveFilterItem } from './activity';

type FixedStateValue = string | number;

interface EnsureFixedStateFilterModelOptions<TStateValue extends FixedStateValue> {
  field: string;
  defaultFilterItem: GridFilterItem;
  defaultStateValue: TStateValue;
  statePriority: readonly TStateValue[];
}

interface GetFixedStateFromFilterModelOptions<
  TStateValue extends FixedStateValue
> {
  field: string;
  fallbackState: TStateValue;
  statePriority: readonly TStateValue[];
}

const toStateCandidates = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [value];

const ensureFilterItemIds = (
  items: GridFilterItem[]
): GridFilterItem[] =>
  items.map((item, index) => {
    if (item.id !== undefined && item.id !== null) {
      return item;
    }

    return {
      ...item,
      id: `${item.field ?? 'field'}-${item.operator ?? 'operator'}-${index}`,
    };
  });

export const toFixedStateValue = <TStateValue extends FixedStateValue>(
  value: unknown,
  statePriority: readonly TStateValue[],
  fallbackState: TStateValue
): TStateValue => {
  const stateSet = new Set(toStateCandidates(value));

  return (
    statePriority.find((state) => stateSet.has(state)) ??
    fallbackState
  );
};

export const ensureFixedStateFilterModel = <
  TStateValue extends FixedStateValue
>(
  model: GridFilterModel,
  options: EnsureFixedStateFilterModelOptions<TStateValue>
): GridFilterModel => {
  const items = model?.items || [];
  const defaultStateValue = toFixedStateValue(
    options.defaultStateValue,
    options.statePriority,
    options.defaultStateValue
  );
  const stateItem = items.find(
    (item) => item.field === options.field
  );

  const fixedStateItem: GridFilterItem = {
    ...options.defaultFilterItem,
    id:
      stateItem?.id ??
      options.defaultFilterItem.id ??
      `fixed-${options.field}`,
    value: toFixedStateValue(
      stateItem?.value,
      options.statePriority,
      defaultStateValue
    ),
  };

  const otherItems = items.filter(
    (item) => item.field !== options.field
  );

  return {
    ...model,
    items: ensureFilterItemIds([fixedStateItem, ...otherItems]),
  };
};

export const getFixedStateFromFilterModel = <
  TStateValue extends FixedStateValue
>(
  model: GridFilterModel,
  options: GetFixedStateFromFilterModelOptions<TStateValue>
): TStateValue => {
  const stateItem = (model?.items ?? []).find(
    (item) => item.field === options.field
  );

  return toFixedStateValue(
    stateItem?.value,
    options.statePriority,
    options.fallbackState
  );
};

export const hasOnlyFixedStateFilter = (
  model: GridFilterModel | undefined,
  field: string
): boolean => {
  const items = model?.items ?? [];
  return items.length === 1 && items.every((item) => item.field === field);
};

export const countActiveFilters = (
  model: GridFilterModel | undefined
): number => {
  const items = model?.items ?? [];
  return items.filter(isActiveFilterItem).length;
};

export const countFiltersExcludingField = (
  model: GridFilterModel | undefined,
  excludedField: string
): number => {
  const items = model?.items ?? [];
  return items.filter(
    (item) => item.field !== excludedField && isActiveFilterItem(item)
  ).length;
};
